<div class="flex items-center justify-between py-4">
  <h1 class="text-xl sm:text-xl font-semibold text-[#444A6D]">
    {{ 'CNIC_EXPIRY.TITLE' | translate }}
  </h1>
</div>

<!-- TABS -->
<div class="bg-gray-200 rounded-lg w-full shadow p-2 mb-4">
  <div class="flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide flex-nowrap">
    <a class="tab cursor-pointer" [class.active-tab]="activeTab === 'expiring'" (click)="switchTab('expiring')">
      {{ 'CNIC_EXPIRY.EXPIRING_TAB' | translate }}
    </a>
    <a class="tab cursor-pointer" [class.active-tab]="activeTab === 'updated'" (click)="switchTab('updated')">
      {{ 'CNIC_EXPIRY.UPDATED_TAB' | translate }}
    </a>
  </div>
</div>

<section class="p-4">
  <!-- EXPIRING CNIC -->
  @if (activeTab === 'expiring') {
    <app-table
      [columns]="expiringColumns"
      [data]="expiringList"
      [actions]="expiringActions"
      [enablePagination]="true"
      [pageSize]="10"
    >
    </app-table>
  }

  <!-- UPDATED CNIC (pending approval) -->
  @if (activeTab === 'updated') {
    <app-table
      [columns]="updatedColumns"
      [data]="updatedList"
      [actions]="updatedActions"
      [enablePagination]="true"
      [pageSize]="10"
    >
    </app-table>
  }
</section>

<!-- UPDATE CNIC POPUP — Relationship Officer (LNLY_RO) -->
<app-popup
  [(show)]="showUpdatePopup"
  title="CNIC_EXPIRY.UPDATE_TITLE"
  type="custom"
  width="w-full max-w-3xl min-w-[300px]"
  [showCustomFooter]="true"
  primaryButtonText="COMMON.SUBMIT"
  [primaryButtonDisabled]="isSubmitting"
  (primaryClick)="submitCnic()"
>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
    <div>
      <label class="form-label">{{ 'CNIC_EXPIRY.CNIC' | translate }}</label>
      <input type="text" class="form-input bg-gray-100 cursor-not-allowed" [value]="form.cnicNum" readonly />
    </div>

    <div>
      <label class="form-label">{{ 'CNIC_EXPIRY.ISSUE_DATE' | translate }} <span class="text-red-600">*</span></label>
      <input type="date" class="form-input" [(ngModel)]="form.cnicIssueDt" [max]="today" />
      @if (submitted && !form.cnicIssueDt) {
        <p class="form-error-text">{{ 'CNIC_EXPIRY.FIELD_REQUIRED' | translate }}</p>
      }
    </div>

    <div>
      <label class="form-label">{{ 'CNIC_EXPIRY.EXPIRY_DATE' | translate }} <span class="text-red-600">*</span></label>
      <input type="date" class="form-input" [(ngModel)]="form.cnicExpryDt" [min]="today" [disabled]="form.isLifetime" />
      @if (submitted && isExpiryMissing) {
        <p class="form-error-text">{{ 'CNIC_EXPIRY.FIELD_REQUIRED' | translate }}</p>
      }
    </div>

    <div class="flex items-center sm:pt-7">
      <label class="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" class="h-4 w-4" [(ngModel)]="form.isLifetime" (change)="onLifetimeChange()" />
        {{ 'CNIC_EXPIRY.LIFETIME_CNIC' | translate }}
      </label>
    </div>

    <!-- CNIC DOCUMENT -->
    <div class="sm:col-span-2">
      <label class="form-label">{{ 'CNIC_EXPIRY.CNIC_DOCUMENT' | translate }} <span class="text-red-600">*</span></label>
      <input #docInput type="file" class="hidden" [accept]="ACCEPTED_FILE_TYPES" (change)="onFileSelected($event)" />

      @if (docFile) {
        <div class="rounded-xl border border-gray-300 overflow-hidden bg-[#FAFAFB] min-h-24 flex items-center justify-center">
          @if (docPreview) {
            <img [src]="docPreview" class="w-full max-h-64 object-contain" alt="" />
          } @else {
            <span class="px-4 py-6 text-sm text-gray-700 break-all" dir="ltr">{{ docFile.name }}</span>
          }
        </div>
        <div class="flex gap-4 mt-2 text-sm">
          <button type="button" class="text-indigo-600 font-medium hover:underline" (click)="docInput.click()">
            {{ 'CNIC_EXPIRY.CHANGE_FILE' | translate }}
          </button>
          <button type="button" class="text-red-600 font-medium hover:underline" (click)="clearFile()">
            {{ 'COMMON.DELETE' | translate }}
          </button>
        </div>
      } @else {
        <button type="button"
          class="w-full h-32 rounded-xl border border-dashed border-gray-300 bg-[#FAFAFB] text-sm text-gray-500 hover:border-indigo-600 hover:text-indigo-600 transition"
          [class.border-red-500]="submitted"
          (click)="docInput.click()">
          + {{ 'COMMON.UPLOAD' | translate }}
        </button>
        <p class="mt-1 text-xs text-gray-400">{{ 'CNIC_EXPIRY.FILE_HINT' | translate }}</p>
      }
    </div>
  </div>
</app-popup>

<!-- CNIC APPROVAL POPUP — Approve is Credit Manager (LNLY_CM) only -->
<app-popup
  [(show)]="showApprovalPopup"
  title="CNIC_EXPIRY.APPROVAL_TITLE"
  type="custom"
  width="w-full max-w-3xl min-w-[300px]"
  [showCustomFooter]="true"
  [primaryButtonText]="isCreditManager ? 'COMMON.APPROVE' : null"
  [primaryButtonDisabled]="isApproving"
  (primaryClick)="approveCnic()"
>
  @if (selectedUpdated) {
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
      <div>
        <label class="form-label">{{ 'CNIC_EXPIRY.CNIC' | translate }}</label>
        <input type="text" class="form-input bg-gray-100 cursor-not-allowed" [value]="selectedUpdated.cnicNum || ''" readonly />
      </div>
      <div>
        <label class="form-label">{{ 'CNIC_EXPIRY.ISSUE_DATE' | translate }}</label>
        <input type="text" class="form-input bg-gray-100 cursor-not-allowed" dir="ltr"
          [value]="(selectedUpdated.cnicIssueDt | date: 'dd-MMM-yyyy') || ''" readonly />
      </div>
      <div>
        <label class="form-label">{{ 'CNIC_EXPIRY.EXPIRY_DATE' | translate }}</label>
        <input type="text" class="form-input bg-gray-100 cursor-not-allowed" [value]="selectedUpdated.expiryDisplay" readonly />
      </div>
    </div>

    <p class="form-label">{{ 'CNIC_EXPIRY.CNIC_DOCUMENT' | translate }}</p>
    <div class="rounded-xl border border-gray-300 overflow-hidden bg-[#FAFAFB] min-h-44 flex items-center justify-center">
      @if (!approvalDocSrc) {
        <span class="text-sm text-gray-400">{{ 'CNIC_EXPIRY.NO_DOCUMENT' | translate }}</span>
      } @else if (approvalDocIsImage) {
        <img [src]="approvalDocSrc" class="w-full max-h-96 object-contain" alt="" />
      } @else {
        <button type="button" class="btn-primary" (click)="viewDocument()">
          {{ 'CNIC_EXPIRY.VIEW_DOCUMENT' | translate }}
        </button>
      }
    </div>
  }
</app-popup>
